import {
  OperatorStatistics,
  OperatorItemStatistics,
  ItemStatistics,
  PickingStatisticsResponse,
  PickingStatisticsFilters
} from '../types/statistics';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class StatisticsService {
  /**
   * Get company number from authenticated user
   */
  private getCompanyNo(): string {
    const userData = AuthService.getUserDataFromToken();
    if (!userData?.companyNo) {
      throw new Error('Ingen brukerdata tilgjengelig');
    }
    return userData.companyNo;
  }

  /**
   * Format a Date as a local calendar date (yyyy-MM-dd) for the API query.
   *
   * IMPORTANT: Do NOT use toISOString() here. toISOString() converts to UTC,
   * which shifts the date by the timezone offset (1-2h in Norway). Picking
   * 25.06 would then be sent as 24.06 or with a 00:00Z boundary that excludes
   * the whole selected day, since stored StartDateTime/StopDateTime are local
   * time. Sending the local calendar date lets the backend apply whole-day
   * boundaries correctly.
   */
  formatDateParam(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get statistics aggregated by operator
   * @param startDate Start date
   * @param endDate End date
   * @returns Array of operator statistics
   */
  async getOperatorStatistics(startDate: Date, endDate: Date): Promise<OperatorStatistics[]> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('startDate', this.formatDateParam(startDate));
    params.append('endDate', this.formatDateParam(endDate));

    const url = `${API_BASE_URL}/${companyNo}/statistics/operator?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer har tilgang til statistikk');
      }
      throw new Error(`Kunne ikke hente operatørstatistikk: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get statistics aggregated by operator and item
   * @param startDate Start date
   * @param endDate End date
   * @returns Array of operator-item statistics
   */
  async getOperatorItemStatistics(startDate: Date, endDate: Date): Promise<OperatorItemStatistics[]> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('startDate', this.formatDateParam(startDate));
    params.append('endDate', this.formatDateParam(endDate));

    const url = `${API_BASE_URL}/${companyNo}/statistics/operator-item?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer har tilgang til statistikk');
      }
      throw new Error(`Kunne ikke hente operatør-varestatistikk: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get statistics aggregated by item
   * @param startDate Start date
   * @param endDate End date
   * @returns Array of item statistics
   */
  async getItemStatistics(startDate: Date, endDate: Date): Promise<ItemStatistics[]> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('startDate', this.formatDateParam(startDate));
    params.append('endDate', this.formatDateParam(endDate));

    const url = `${API_BASE_URL}/${companyNo}/statistics/item?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer har tilgang til statistikk');
      }
      throw new Error(`Kunne ikke hente varestatistikk: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Export statistics to CSV format
   * @param data Statistics data to export
   * @param filename Filename for the export
   */
  exportToCSV<T extends Record<string, any>>(data: T[], filename: string): void {
    if (data.length === 0) {
      throw new Error('Ingen data å eksportere');
    }

    // Get column headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
      headers.join(';'), // Header row
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          // Handle numbers with decimal separator
          if (typeof value === 'number') {
            return value.toString().replace('.', ',');
          }
          // Escape strings with quotes if they contain semicolons
          if (typeof value === 'string' && value.includes(';')) {
            return `"${value}"`;
          }
          return value || '';
        }).join(';')
      )
    ].join('\n');

    // Add BOM for Norwegian characters in Excel
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Hent plukkstatistikk: én rad per operatør, vektede totaler og trendserier.
   *
   * Grenseverdier som ikke sendes med hentes fra innstillinger på serversiden,
   * så det er trygt å utelate dem.
   */
  async getPickingStatistics(
    startDate: Date,
    endDate: Date,
    filters: PickingStatisticsFilters
  ): Promise<PickingStatisticsResponse> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('startDate', this.formatDateParam(startDate));
    params.append('endDate', this.formatDateParam(endDate));
    params.append('interval', String(filters.interval));

    if (filters.workLimitMinutes) {
      params.append('workLimitMinutes', String(filters.workLimitMinutes));
    }
    if (filters.upperLimitMinutes) {
      params.append('upperLimitMinutes', String(filters.upperLimitMinutes));
    }
    if (filters.deadTimeGapMinutes) {
      params.append('deadTimeGapMinutes', String(filters.deadTimeGapMinutes));
    }
    if (filters.minSampleLines) {
      params.append('minSampleLines', String(filters.minSampleLines));
    }
    // Gjentatt nøkkel er formatet ASP.NET Core binder til string[]
    filters.zones.forEach(z => params.append('zones', z));
    filters.operators.forEach(o => params.append('operators', o));

    const url = `${API_BASE_URL}/${companyNo}/statistics/picking?${params.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer har tilgang til statistikk');
      }
      if (response.status === 400) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Ugyldig periode eller parametere');
      }
      throw new Error(`Kunne ikke hente plukkstatistikk: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Format date for display (Norwegian format)
   * @param date Date to format
   * @returns Formatted date string
   */
  formatDate(date: Date): string {
    return date.toLocaleDateString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Format number for display (Norwegian format)
   * @param value Number to format
   * @param decimals Number of decimal places
   * @returns Formatted number string
   */
  formatNumber(value: number, decimals: number = 2): string {
    return value.toLocaleString('no-NO', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  /**
   * Format currency for display (Norwegian format)
   * @param value Currency value
   * @returns Formatted currency string
   */
  formatCurrency(value: number): string {
    return value.toLocaleString('no-NO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' kr';
  }
}

export default new StatisticsService();
